import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Button from '@mui/material/Button';
import { useGlobalStore } from '../../stores/globalStore';
import { removeUserPref } from '../../hooks/useUserPreferences';
import { readUserState, useServerUserState, writeUserState } from '../../hooks/useServerUserState';
import { useWelcomeTourStore, type TourProgress } from './welcomeTourStore';
import {
  WELCOME_TOUR_PREF,
  WELCOME_TOUR_VERSION,
  shouldAutoShowTour,
  tourPages,
  type WelcomeTourRecord,
} from './welcomeTourModel';
import WelcomeTour from './WelcomeTour';
import './WelcomeTour.css';

/** Where the dialog this tour replaced kept its "dismissed" version. */
const OLD_WELCOME_PREF = 'welcome.dismissed';

/**
 * Mounted once in MainLayout (only once the user, facility and role have loaded). Opens the welcome
 * tour the first time the user reaches the website; Help and My Settings open it again
 * (welcomeTourStore). While a help article opened from the tour is on screen, a small bar at the
 * bottom offers the way back to the same page of the tour.
 *
 * It counts as seen the moment it opens, so leaving part way (Skip tour, Esc, closing the browser)
 * never brings it back. The record is kept with the user on the server and in this browser
 * (useServerUserState), so it opens once per person, not once per browser; it waits until the
 * server's copy has loaded (or failed) so it never opens for someone who saw it elsewhere.
 */
export default function WelcomeTourHost() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const userId = useGlobalStore((s) => s.userId);
  const role = useGlobalStore((s) => s.userRole);
  const open = useWelcomeTourStore((s) => s.open);
  const paused = useWelcomeTourStore((s) => s.paused);
  const resumeFrom = useWelcomeTourStore((s) => s.resumeFrom);
  const show = useWelcomeTourStore((s) => s.show);
  const pause = useWelcomeTourStore((s) => s.pause);
  const resume = useWelcomeTourStore((s) => s.resume);
  const hide = useWelcomeTourStore((s) => s.hide);
  const { ready: stateReady } = useServerUserState();

  useEffect(() => {
    if (open || paused || userId <= 0 || !stateReady) return;
    const record = readUserState<WelcomeTourRecord | null>(userId, WELCOME_TOUR_PREF, null);
    if (!shouldAutoShowTour({ ready: true, pathname, role, record })) return;
    writeUserState<WelcomeTourRecord>(userId, WELCOME_TOUR_PREF, {
      version: WELCOME_TOUR_VERSION,
      shownAt: new Date().toISOString(),
      outcome: 'skipped',
    });
    // The old one-page "Welcome to Dietech 2025.1" dialog's record; nothing reads it now.
    removeUserPref(userId, OLD_WELCOME_PREF);
    show();
  }, [open, paused, show, userId, role, pathname, stateReady]);

  const close = (outcome: 'finished' | 'skipped') => {
    const record = readUserState<WelcomeTourRecord | null>(userId, WELCOME_TOUR_PREF, null);
    writeUserState<WelcomeTourRecord>(userId, WELCOME_TOUR_PREF, {
      version: WELCOME_TOUR_VERSION,
      shownAt: record?.shownAt ?? new Date().toISOString(),
      outcome,
    });
    hide();
  };

  const leave = (progress: TourProgress, to: string) => {
    pause(progress);
    navigate(to);
  };

  if (open) return <WelcomeTour onClose={close} onLeave={leave} initial={resumeFrom} />;
  if (paused) return <TourPausedBar progress={paused} onResume={resume} onEnd={() => close('skipped')} />;
  return null;
}

/** The way back to the tour while an article from it is open. */
function TourPausedBar({ progress, onResume, onEnd }: { progress: TourProgress; onResume: () => void; onEnd: () => void }) {
  const total = tourPages(progress.answer).length;
  return (
    <div className="welcome-tour-paused" role="region" aria-label="Welcome tour">
      <span className="welcome-tour-paused__text">Welcome tour, step {progress.index + 1} of {total}</span>
      <span className="welcome-tour-paused__actions">
        <Button variant="text" size="small" onClick={onEnd}>End tour</Button>
        <Button variant="contained" size="small" onClick={onResume}>Back to the tour</Button>
      </span>
    </div>
  );
}
